import { hasIncompleteTourItem, hasInsuranceItem, isAddFlightsStepAvailable, isAirportTransfersStepAvailable, isExperiencesSupportedToAddToCart, isFlightsPendingToAddToCart, isLuxPlusStandaloneStepAvailable } from 'checkout/selectors/navigationSelectors'
import { CheckoutPageId } from 'checkout/constants/pages'
import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import { getInflowFlightSearchParams } from 'checkout/selectors/flightSearchSelectors'
import { removeItem } from 'actions/CheckoutActions'
import config from 'constants/config'
import { getCharterFlightItems, getNonCharterFlightItems } from 'checkout/selectors/view/flights'
import { showCheckoutCartModeModal } from 'actions/UiActions'
import { getCheckoutPathWithCartId } from 'lib/url/pathsUtils'
import { navigateToNextStepTourCheckout } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/tours'
import { navigateToNextStepCruiseCheckout } from 'actions/CruiseActions'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import type { AppAction } from 'actions/types/ActionTypes'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import { getCurrentCheckoutPage } from 'checkout/lib/navigation/getCurrentCheckoutPage'
import { getSubscriptionJoinItems, isLuxPlusSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscriptionItems'
import { buildFunnelCartAttributes } from 'checkout/selectors/funnel'
import { refreshFunnelOptimiserFlags } from 'actions/SystemActions'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { fetchFlightJourneyV2 } from 'actions/FlightActions'
import { getCheckoutFlightItemFinalFare } from 'actions/CheckoutFlightActions'
import { getShouldShowFlightsBeforeLuxPlusUpsell, getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { getIsMobileAppLikeCheckoutEnabled } from 'checkout/selectors/mobileAppLikeCheckoutSelectors'
import { trackCheckoutObservabilityEvent } from 'analytics/snowplow/checkoutAnalytics'

export function skipFlights(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const flightItems = getNonCharterFlightItems(state)
    const skippedFeederBundle = flightItems.some(item => !!item.bundleBudget)
    flightItems.forEach(item => {
      dispatch(removeItem(item.itemId))
    })

    if (skippedFeederBundle) {
      const updatedState = getState()
      const charterFlightItems = getCharterFlightItems(updatedState)
      charterFlightItems.forEach((item, index) => {
        if (item.searchId) {
          item.flights.forEach((flight) => {
            if (flight.journeyId) {
              dispatch(fetchFlightJourneyV2(item.searchId!, flight.journeyId, flight.fareFamily?.id))
            }
          })
        }

        dispatch(getCheckoutFlightItemFinalFare(item, index))
      })
    }

    await dispatch(navigateToNextPage())
  }
}

export function navigateToNextPage(replace?: boolean,
  navigateOptions?: {
    customFlightsSearchParams?: string
    targetPage?: CheckoutPageId
  },
): AppAction {
  return async(dispatch, getState) => {
    // Refresh funnel optimiser flags with current cart state before determining next page
    // This ensures step visibility decisions use the latest cart attributes
    const stateBeforeRefresh = getState()
    await dispatch(refreshFunnelOptimiserFlags(buildFunnelCartAttributes(stateBeforeRefresh)))

    const state = getState()
    const { customFlightsSearchParams, targetPage } = navigateOptions ?? {}
    trackCheckoutObservabilityEvent({
      subject: 'step',
      action: 'continuation_ready',
      type: 'operational',
      scope: 'cart',
      canContinue: true,
    })
    // let's work out what product flow on the checkout we are in
    const isTourV2 = getTourV2Items(state).length > 0
    const hasIncompleteTourV1Item = hasIncompleteTourItem(state)
    if (isTourV2 && !hasIncompleteTourV1Item) {
      // Must be a tour flow, use the tour step logic
      dispatch(navigateToNextStepTourCheckout(replace))
      return
    }

    const isCruise = getCruiseItems(state).length > 0
    if (isCruise) {
      // Must be a cruise flow, use the cruise step logic
      dispatch(navigateToNextStepCruiseCheckout(replace))
      return
    }

    // Must be a hotel flow, use the hotel step logic
    // Todo: Add support for other product flows
    const page = targetPage ?? getNextCheckoutPage(state)
    const search = customFlightsSearchParams ?? getSearchParams(state, page)
    const cartId = state.checkout.cart.cartId

    const url = getCheckoutPathWithCartId(page, cartId)

    if (replace) {
      dispatch(replaceWithRegion(url, search))
    } else {
      dispatch(pushWithRegion(url, search))
    }
  }
}

interface NavigateToCheckoutOptions {
  replace?: boolean
  skipSpoofedModal?: boolean
  replaceCurrentWith?: string
}

export function navigateToCheckout({
  replace,
  skipSpoofedModal,
  replaceCurrentWith,
}: NavigateToCheckoutOptions = {}): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    // Replace current history entry first (if specified)
    // Can be used for removing query params before navigating to checkout
    if (replaceCurrentWith) {
      dispatch(replaceWithRegion(replaceCurrentWith))
    }

    if (
      config.UNIVERSAL_CHECKOUT_SPOOFED_MULTI_CART_ITEMS_ENABLED &&
      state.checkout.cart.isMultiItemMode &&
      !skipSpoofedModal
    ) {
      dispatch(showCheckoutCartModeModal())
    } else {
      dispatch(navigateToNextPage(replace))
    }
  }
}

export const restartCheckout = navigateToNextPage

export function navigateToHotelCheckoutStep(stepId: CheckoutPageId, replace?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const search = getSearchParams(state, stepId)
    const cartId = state.checkout.cart.cartId
    const url = getCheckoutPathWithCartId(stepId, cartId)

    if (replace) {
      dispatch(replaceWithRegion(url, search))
    } else {
      dispatch(pushWithRegion(url, search))
    }
  }
}

function getNextCheckoutPage(state: App.State): CheckoutPageId {
  const curPage = getCurrentCheckoutPage(state)
  const hasLuxPlusCheckoutItems = isLuxPlusSubscriptionInCart(state) || getSubscriptionJoinItems(state).length > 0
  const isFreePreviewLuxPlusMember = getIsFreePreviewLuxPlusMember(state)
  const shouldShowFlightsBeforeLuxPlusUpsell = getShouldShowFlightsBeforeLuxPlusUpsell(state)
  const shouldNavigateToLuxPlusStep = isLuxPlusStandaloneStepAvailable(state) ||
    (isFreePreviewLuxPlusMember && hasLuxPlusCheckoutItems)
  const isFlightStep = curPage === CheckoutPageId.AddFlights ||
    curPage === CheckoutPageId.DepartingFlights ||
    curPage === CheckoutPageId.ReturningFlights
  const shouldDeferLuxPlusUntilAfterFlights = shouldShowFlightsBeforeLuxPlusUpsell && (
    isAddFlightsStepAvailable(state) ||
    isFlightsPendingToAddToCart(state) ||
    curPage === CheckoutPageId.AddFlights ||
    curPage === CheckoutPageId.DepartingFlights ||
    curPage === CheckoutPageId.ReturningFlights
  )

  if (
    curPage !== CheckoutPageId.LuxPlus &&
    curPage !== CheckoutPageId.AddFlights &&
    curPage !== CheckoutPageId.DepartingFlights &&
    curPage !== CheckoutPageId.AirportTransfers &&
    curPage !== CheckoutPageId.Experiences &&
    curPage !== CheckoutPageId.ReturningFlights &&
    shouldNavigateToLuxPlusStep &&
    !shouldDeferLuxPlusUntilAfterFlights
  ) {
    return CheckoutPageId.LuxPlus
  }

  if (
    curPage !== CheckoutPageId.AddFlights &&
    curPage !== CheckoutPageId.DepartingFlights &&
    curPage !== CheckoutPageId.ReturningFlights &&
    curPage !== CheckoutPageId.AirportTransfers &&
    curPage !== CheckoutPageId.Experiences &&
    !(curPage === CheckoutPageId.LuxPlus && shouldShowFlightsBeforeLuxPlusUpsell) &&
    isAddFlightsStepAvailable(state) &&
    !isFlightsPendingToAddToCart(state)
  ) {
    return CheckoutPageId.AddFlights
  }

  if (
    curPage !== CheckoutPageId.AddFlights &&
    curPage !== CheckoutPageId.DepartingFlights &&
    curPage !== CheckoutPageId.ReturningFlights &&
    curPage !== CheckoutPageId.AirportTransfers &&
    curPage !== CheckoutPageId.Experiences &&
    isAddFlightsStepAvailable(state) &&
    isFlightsPendingToAddToCart(state) &&
    getCharterFlightItems(state).length > 0
  ) {
    // Charter feeder flights are already added to the cart, so take customers straight
    // to their selection. The departing page still lets them skip connecting flights.
    return CheckoutPageId.DepartingFlights
  }

  if (
    curPage !== CheckoutPageId.AddFlights &&
    curPage !== CheckoutPageId.DepartingFlights &&
    curPage !== CheckoutPageId.ReturningFlights &&
    curPage !== CheckoutPageId.AirportTransfers &&
    curPage !== CheckoutPageId.Experiences &&
    isAddFlightsStepAvailable(state) &&
    isFlightsPendingToAddToCart(state)
  ) {
    return CheckoutPageId.AddFlights
  }

  if (
    curPage !== CheckoutPageId.DepartingFlights &&
    curPage !== CheckoutPageId.ReturningFlights &&
    curPage !== CheckoutPageId.AirportTransfers &&
    curPage !== CheckoutPageId.Experiences &&
    isAddFlightsStepAvailable(state) &&
    isFlightsPendingToAddToCart(state)
  ) {
    return CheckoutPageId.DepartingFlights
  }

  if (
    isFlightStep &&
    shouldShowFlightsBeforeLuxPlusUpsell &&
    shouldNavigateToLuxPlusStep &&
    !isFlightsPendingToAddToCart(state)
  ) {
    return CheckoutPageId.LuxPlus
  }
  if (curPage !== CheckoutPageId.TourV1Capacity && hasIncompleteTourItem(state)) {
    return CheckoutPageId.TourV1Capacity
  }

  if (config.EXPERIENCES_ENABLED && curPage !== CheckoutPageId.Experiences && curPage !== CheckoutPageId.AirportTransfers && isExperiencesSupportedToAddToCart(state)) {
    return CheckoutPageId.Experiences
  }
  if (
    curPage !== CheckoutPageId.AirportTransfers &&
    isAirportTransfersStepAvailable(state)
  ) {
    return CheckoutPageId.AirportTransfers
  }

  if (
    (curPage === CheckoutPageId.BedbankChangeDates ||
      curPage === CheckoutPageId.BedbankChangePackage ||
      curPage === CheckoutPageId.ChangeDates ||
      curPage === CheckoutPageId.ChangePackage) &&
      hasInsuranceItem(state)
  ) {
    return CheckoutPageId.InsuranceSelectDates
  }
  if (curPage === CheckoutPageId.AddExtraGuests && hasInsuranceItem(state)) {
    return CheckoutPageId.InsuranceAddExtraGuests
  }

  // Handle flight-change-dates mode: route to insurance step if order has insurance
  const postPurchaseMode = findPostPurchaseCheckout(state.checkout.cart.mode)
  if (
    postPurchaseMode === 'flight-change-dates' &&
    curPage !== CheckoutPageId.InsuranceSelectDates &&
    hasInsuranceItem(state)
  ) {
    return CheckoutPageId.InsuranceSelectDates
  }

  if (getIsMobileAppLikeCheckoutEnabled(state)) {
    if (curPage === CheckoutPageId.Review) {
      return CheckoutPageId.Payment
    }

    return CheckoutPageId.Review
  }

  return CheckoutPageId.Purchase
}

function getSearchParams(state: App.State, page: CheckoutPageId): string | undefined {
  switch (page) {
    case CheckoutPageId.DepartingFlights:
      return getInflowFlightSearchParams(state)
  }
}
